import { useMutation, useQuery, useQueryClient } from 'react-query';
import { datadogLogs } from '@datadog/browser-logs';
import { IProject } from '../../interfaces/project.interface';
import { baseApi, buildAuthHeaders, throwApiError, useRetrieveToken } from './shared';

export const useProjectApi = () => {
  const { retrieveToken, isAuthenticated } = useRetrieveToken();
  const queryClient = useQueryClient();

  const createProject = useMutation(async (projectData: IProject) => {
    const token = await retrieveToken('createProject');
    if (!token) return;

    const response = await fetch(`${baseApi}/project`, {
      method: 'POST',
      headers: buildAuthHeaders(token),
      body: JSON.stringify(projectData),
    });

    if (!response.ok) await throwApiError(response, 'createProject', { projectData });

    return response.json();
  });

  const updateProjectName = useMutation(
    async ({ projectId, name }: { projectId: string; name: string }) => {
      const token = await retrieveToken('updateProjectName');
      if (!token) return;

      const response = await fetch(`${baseApi}/project/${projectId}/name`, {
        method: 'PATCH',
        headers: buildAuthHeaders(token),
        body: JSON.stringify({ name }),
      });

      if (!response.ok) await throwApiError(response, 'updateProjectName', { payload: { projectId, name } });

      try {
        const responseJson: IProject = await response?.json();
        return responseJson;
      } catch {
        return null;
      }
    },
    {
      // useProjectById is cached for an hour and set not to refetch on mount, so the results page
      // would keep showing — and naming downloads after — the name the project had before. Write
      // the rename through rather than relying on a refetch that will not happen.
      onSuccess: (_result, { projectId, name }) => {
        queryClient.setQueryData<IProject | undefined>(['project', projectId], (project) =>
          project ? { ...project, name } : project,
        );
        queryClient.invalidateQueries(['projects']);
      },
    },
  );

  const updateProjectCode = useMutation(async ({ projectId, code }: { projectId: string; code: string }) => {
    const token = await retrieveToken('updateProjectCode');
    if (!token) return;

    const response = await fetch(`${baseApi}/project/${projectId}/code`, {
      method: 'PATCH',
      headers: buildAuthHeaders(token),
      body: JSON.stringify({ code }),
    });

    if (!response.ok) await throwApiError(response, 'updateProjectCode', { payload: { projectId, code } });

    return true;
  });

  const refundProject = useMutation(
    async ({ projectId, updatedBy }: { projectId: string; updatedBy: string }) => {
      const token = await retrieveToken('refundProject');
      if (!token) return;

      const response = await fetch(`${baseApi}/project/${projectId}/refund`, {
        method: 'PATCH',
        headers: buildAuthHeaders(token),
        body: JSON.stringify({ updatedBy }),
      });

      if (!response.ok) await throwApiError(response, 'refundProject', { payload: { projectId, updatedBy } });

      try {
        const responseJson: IProject = await response.json();
        return responseJson;
      } catch {
        return null;
      }
    },
    { mutationKey: ['refund-project'] },
  );

  const deleteProject = useMutation(async (projectId: string) => {
    const token = await retrieveToken('deleteProject');
    if (!token) return;

    const response = await fetch(`${baseApi}/test-helper/project/${projectId}`, {
      method: 'DELETE',
      headers: buildAuthHeaders(token),
    });

    if (!response.ok) {
      datadogLogs.logger.error('Failed to delete project', {
        name: 'deleteProject',
        response: { status: response.status, statusText: response.statusText },
      });
      throw new Error('Failed to delete project');
    }
  });

  const useProjectById = (projectId: string) => {
    return useQuery<unknown, unknown, IProject, any>(
      ['project', projectId],
      async () => {
        const token = await retrieveToken('getProjectById');
        if (!token) return;

        const response = await fetch(`${baseApi}/project/${projectId}`, {
          method: 'GET',
          headers: buildAuthHeaders(token),
        });

        if (!response.ok) {
          datadogLogs.logger.error('Failed to fetch project data', {
            name: 'useProjectById',
            response: { status: response.status, statusText: response.statusText },
          });
          throw new Error('Failed to fetch project data');
        }

        return response.json();
      },
      { refetchOnWindowFocus: false, refetchOnMount: false, cacheTime: 60 * 60 * 1000 },
    );
  };

  const useProjects = () => {
    return useQuery<unknown, unknown, IProject[], any>(
      ['projects'],
      async ({ signal }) => {
        const token = await retrieveToken('getProjects');
        if (!token) return;

        let response: Response;
        try {
          response = await fetch(`${baseApi}/project/`, {
            method: 'GET',
            headers: buildAuthHeaders(token),
            signal,
          });
        } catch (error) {
          if ((error as Error).name === 'AbortError') return;
          throw error;
        }

        if (!response.ok) {
          datadogLogs.logger.error('Failed to fetch user data', {
            name: 'getProjects',
            response: { status: response.status, statusText: response.statusText },
          });
          throw new Error('Failed to fetch user data');
        }

        return response.json();
      },
      { retryDelay: 5000, enabled: isAuthenticated },
    );
  };

  const useAllProjects = ({ status }: { status: 'has-result' | 'no-result' }) => {
    return useQuery<unknown, unknown, IProject[], string[]>(
      ['all_projects', status],
      async ({ signal }) => {
        const token = await retrieveToken('getAllProjects');
        if (!token) return;

        const response = await fetch(`${baseApi}/project/status/${status}`, {
          method: 'GET',
          headers: buildAuthHeaders(token),
          signal,
        });

        if (!response.ok) {
          datadogLogs.logger.error('Failed to fetch user data', {
            name: 'getAllProjects',
            response: { status: response.status, statusText: response.statusText },
          });
          throw new Error('Failed to fetch user data');
        }

        return response.json();
      },
      { refetchOnWindowFocus: false, refetchOnMount: false, enabled: isAuthenticated },
    );
  };

  const notifyCustomer = useMutation(async (projectId: string) => {
    const token = await retrieveToken('notifyCustomer');
    if (!token) return;

    const response = await fetch(`${baseApi}/project/${projectId}/notify-customer`, {
      method: 'POST',
      headers: buildAuthHeaders(token),
    });

    if (!response.ok) await throwApiError(response, 'notifyCustomer', { projectId });

    return response.json();
  });

  const notifyResellerLimit = useMutation(
    async ({
      projectId,
      resellerEmail,
      usagePercent,
    }: {
      projectId: string;
      resellerEmail: string;
      usagePercent: number;
    }) => {
      const token = await retrieveToken('notifyResellerLimit');
      if (!token) return;

      const response = await fetch(`${baseApi}/project/${projectId}/notify-reseller-limit`, {
        method: 'POST',
        headers: buildAuthHeaders(token),
        body: JSON.stringify({ resellerEmail, usagePercent }),
      });

      if (!response.ok) {
        await throwApiError(response, 'notifyResellerLimit', {
          payload: { projectId, resellerEmail, usagePercent },
        });
      }

      return response.json();
    },
  );

  return {
    createProject,
    updateProjectName,
    updateProjectCode,
    refundProject,
    deleteProject,
    useProjectById,
    useProjects,
    useAllProjects,
    notifyCustomer,
    notifyResellerLimit,
  };
};
